import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Plus, Pencil, Check, X, Trash2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';
import { cn } from '../lib/utils';
import { invalidatePhotographersCache } from './organization/PhotographerField';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * In-house photographer directory modal.
 *
 * Behaviour summary:
 *   - Read-only for non-admins. Admins see edit + delete + add-row controls.
 *   - Each row supports inline edit (name / phone / email).
 *   - The list seeds itself on backend startup the first time, so this modal
 *     should never be empty in production once deployed.
 */
export const PhotographersModal = ({ open, onClose, isAdmin = false, isBarbiePink = false }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '' });
  const [creatingRow, setCreatingRow] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', phone: '', email: '' });
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/photographers`);
      if (!res.ok) {
        toast.error('Failed to load photographers');
        return;
      }
      const data = await res.json();
      setItems(data.data || []);
    } catch (_e) {
      toast.error('Failed to load photographers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startEdit = (row) => {
    setEditingId(row.photographer_id);
    setEditForm({
      name: row.name || '',
      phone: row.phone || '',
      email: row.email || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', phone: '', email: '' });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSavingId(id);
    try {
      const res = await authJsonFetch(`${API}/photographers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return;
      }
      const updated = await res.json();
      setItems((prev) => prev.map((p) => (p.photographer_id === id ? updated : p)));
      invalidatePhotographersCache();
      cancelEdit();
      toast.success('Photographer updated');
    } catch (_e) {
      toast.error('Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.name}? This cannot be undone.`)) return;
    setSavingId(row.photographer_id);
    try {
      const res = await authFetch(`${API}/photographers/${row.photographer_id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Delete failed');
        return;
      }
      setItems((prev) => prev.filter((p) => p.photographer_id !== row.photographer_id));
      invalidatePhotographersCache();
      toast.success(`Deleted ${row.name}`);
    } catch (_e) {
      toast.error('Delete failed');
    } finally {
      setSavingId(null);
    }
  };

  const submitCreate = async () => {
    if (!createForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSavingId('__new__');
    try {
      const res = await authJsonFetch(`${API}/photographers`, {
        method: 'POST',
        body: JSON.stringify(createForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Create failed');
        return;
      }
      const created = await res.json();
      // Insert preserving alphabetical order
      setItems((prev) => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      invalidatePhotographersCache();
      setCreatingRow(false);
      setCreateForm({ name: '', phone: '', email: '' });
      toast.success(`Added ${created.name}`);
    } catch (_e) {
      toast.error('Create failed');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="photographers-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Photographers
          </DialogTitle>
          <DialogDescription>
            In-house photographer directory. {isAdmin
              ? 'Click any row to edit; use the + button to add a new entry.'
              : 'Use the + button to add a new photographer. Contact an admin to edit or remove existing entries.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead className="w-[35%]">Name</TableHead>
                  <TableHead className="w-[25%]">Phone</TableHead>
                  <TableHead className="w-[30%]">Email</TableHead>
                  {/* Actions column is needed whenever admins have edit/delete
                      OR when the create-new row is showing (its save/cancel
                      buttons live in the Actions cell). Non-admins never see
                      per-row actions — only the create-row's Save/Cancel. */}
                  {(isAdmin || creatingRow) && <TableHead className="w-[10%] text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && !creatingRow ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 4 : 3} className="text-center py-8 text-muted-foreground">
                      No photographers yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => {
                    const editing = editingId === row.photographer_id;
                    return (
                      <TableRow key={row.photographer_id} data-testid={`photographer-row-${row.photographer_id}`}>
                        <TableCell>
                          {editing ? (
                            <Input
                              value={editForm.name}
                              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                              data-testid={`photographer-edit-name-${row.photographer_id}`}
                            />
                          ) : (
                            <span className="font-medium">{row.name}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <Input
                              value={editForm.phone}
                              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                              data-testid={`photographer-edit-phone-${row.photographer_id}`}
                            />
                          ) : (
                            <a href={row.phone ? `tel:${row.phone.replace(/\s+/g, '')}` : undefined} className={row.phone ? 'hover:underline' : 'text-muted-foreground'}>
                              {row.phone || '—'}
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <Input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                              placeholder="(optional)"
                              data-testid={`photographer-edit-email-${row.photographer_id}`}
                            />
                          ) : (
                            row.email ? (
                              <a href={`mailto:${row.email}`} className="hover:underline">{row.email}</a>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )
                          )}
                        </TableCell>
                        {(isAdmin || creatingRow) && (
                          <TableCell className="text-right">
                            {/* Non-admins see this column only when the
                                create-new row is open (needed for column
                                alignment). Render an empty cell for existing
                                rows because they don't get edit/delete. */}
                            {!isAdmin ? null : editing ? (
                              <div className="flex items-center gap-1 justify-end">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => saveEdit(row.photographer_id)}
                                  disabled={savingId === row.photographer_id}
                                  data-testid={`photographer-save-${row.photographer_id}`}
                                >
                                  {savingId === row.photographer_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={cancelEdit}
                                  data-testid={`photographer-cancel-${row.photographer_id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 justify-end">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => startEdit(row)}
                                  data-testid={`photographer-edit-${row.photographer_id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDelete(row)}
                                  className="text-destructive hover:text-destructive"
                                  disabled={savingId === row.photographer_id}
                                  data-testid={`photographer-delete-${row.photographer_id}`}
                                >
                                  {savingId === row.photographer_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}

                {creatingRow && (
                  <TableRow data-testid="photographer-new-row">
                    <TableCell>
                      <Input
                        autoFocus
                        placeholder="Name"
                        value={createForm.name}
                        onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                        data-testid="photographer-new-name"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Phone"
                        value={createForm.phone}
                        onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                        data-testid="photographer-new-phone"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Email (optional)"
                        type="email"
                        value={createForm.email}
                        onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                        data-testid="photographer-new-email"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={submitCreate}
                          disabled={savingId === '__new__'}
                          data-testid="photographer-new-save"
                        >
                          {savingId === '__new__' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => { setCreatingRow(false); setCreateForm({ name: '', phone: '', email: '' }); }}
                          data-testid="photographer-new-cancel"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-muted-foreground">
              {items.length} photographer{items.length === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              {/* Add is available to ALL authenticated users (Jun 19 2026).
                  Backend PhotographerCreate now allows non-admins; edit/
                  delete remain admin-only. */}
              {!creatingRow && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCreatingRow(true)}
                  className={cn(isBarbiePink && 'border-pink-500')}
                  data-testid="photographer-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="photographers-close-btn">
                Close
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotographersModal;
